import React, { useEffect, useState } from 'react';
import { Award, BarChart2, CheckCircle, AlertTriangle, ThumbsUp, ThumbsDown, BrainCircuit, User, X, Code, Volume2, Clock, Layers, BarChart3, Briefcase, Users, Palette, TrendingUp, Shield, Star, MessageCircle, BookOpen, Calendar, DollarSign, Megaphone, Heart, GraduationCap, Scale, Info } from 'lucide-react';
import { Button } from '../common/Button';
import ModalPortal from '../common/ModalPortal';
import { InterviewFeedback } from '../../types/interviews';
import FeedbackRatingCard from './FeedbackRatingCard';
import interviewService from '../../services/interviewService';
import { useToast } from '../common/Toast';
import toast from 'react-hot-toast';
import { buildLearnifieGoal, generateLearnifieLink } from '../../utils/learnifie';

interface InterviewFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: InterviewFeedback;
}

// Helper function to get the appropriate icon component
const getIconComponent = (iconName: string, size: number = 16, className: string = '') => {
  console.log('[InterviewFeedbackModal] getIconComponent', iconName);
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    Code,
    BrainCircuit,
    Volume2,
    Clock,
    Layers,
    BarChart3,
    Briefcase,
    Users,
    Palette,
    TrendingUp,
    Shield,
    CheckCircle,
    Star,
    MessageCircle,
    BookOpen,
    Calendar,
    DollarSign,
    Megaphone,
    Heart,
    GraduationCap,
    Scale,
    BarChart2,
    Award
  };

  const IconComponent = iconMap[iconName] || Star; // Default to Star if icon not found
  return React.createElement(IconComponent, { size, className });
};

// Helper function to assign icon and color to categories that don't have them
const assignCategoryIconAndColor = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  
  // Technical and Development related
  if (name.includes('technical') || name.includes('code') || name.includes('programming') || 
      name.includes('development') || name.includes('software') || name.includes('algorithm') ||
      name.includes('coding') || name.includes('implementation') || name.includes('debugging') ||
      name.includes('refactoring') || name.includes('optimization')) {
    return { icon: 'Code', color: 'bg-blue-500' };
  }
  
  // Problem Solving and Logic
  if (name.includes('problem') || name.includes('solving') || name.includes('logic') || 
      name.includes('analytical') || name.includes('critical') || name.includes('reasoning') ||
      name.includes('thinking') || name.includes('approach') || name.includes('methodology') ||
      name.includes('troubleshooting') || name.includes('decision')) {
    return { icon: 'BrainCircuit', color: 'bg-purple-500' };
  }
  
  // Communication and Presentation
  if (name.includes('communication') || name.includes('presentation') || name.includes('verbal') || 
      name.includes('explanation') || name.includes('articulation') || name.includes('clarity') ||
      name.includes('speaking') || name.includes('listening') || name.includes('interpersonal') ||
      name.includes('writing') || name.includes('documentation') || name.includes('storytelling')) {
    return { icon: 'Volume2', color: 'bg-green-500' };
  }
  
  // Time Management and Organization
  if (name.includes('time') || name.includes('management') || name.includes('organization') || 
      name.includes('efficiency') || name.includes('planning') || name.includes('deadline') ||
      name.includes('scheduling') || name.includes('prioritization') || name.includes('workflow') ||
      name.includes('productivity') || name.includes('execution')) {
    return { icon: 'Clock', color: 'bg-orange-500' };
  }
  
  // System Design and Architecture
  if (name.includes('system') || name.includes('design') || name.includes('architecture') || 
      name.includes('scalability') || name.includes('infrastructure') || name.includes('distributed') ||
      name.includes('microservices') || name.includes('database') || name.includes('performance') ||
      name.includes('reliability') || name.includes('availability') || name.includes('consistency') ||
      name.includes('backend')) {
    return { icon: 'Layers', color: 'bg-indigo-500' };
  }
  
  // Data and Analytics
  if (name.includes('data') || name.includes('analytics') || name.includes('analysis') || 
      name.includes('statistics') || name.includes('machine learning') || name.includes('ai') ||
      name.includes('modeling') || name.includes('visualization') || name.includes('insights') ||
      name.includes('metrics') || name.includes('reporting') || name.includes('intelligence')) {
    return { icon: 'BarChart3', color: 'bg-cyan-500' };
  }
  
  // Business and Strategy
  if (name.includes('business') || name.includes('strategy') || name.includes('stakeholder') || 
      name.includes('product') || name.includes('vision') || name.includes('roadmap') ||
      name.includes('market') || name.includes('commercial') || name.includes('revenue') ||
      name.includes('growth') || name.includes('planning') || name.includes('objectives')) {
    return { icon: 'Briefcase', color: 'bg-emerald-500' };
  }
  
  // Leadership and Teamwork
  if (name.includes('leadership') || name.includes('team') || name.includes('collaboration') || 
      name.includes('mentor') || name.includes('coaching') || name.includes('delegation') ||
      name.includes('coordination') || name.includes('facilitation') || name.includes('influence') ||
      name.includes('motivation') || name.includes('conflict') || name.includes('consensus')) {
    return { icon: 'Users', color: 'bg-pink-500' };
  }
  
  // Creativity and Innovation
  if (name.includes('creative') || name.includes('innovation') || name.includes('ideation') || 
      name.includes('user experience') || name.includes('ux') || name.includes('ui') ||
      name.includes('visual') || name.includes('aesthetic') || name.includes('artistic') ||
      name.includes('originality') || name.includes('imagination') || name.includes('invention')) {
    return { icon: 'Palette', color: 'bg-rose-500' };
  }
  
  // Security and Compliance
  if (name.includes('security') || name.includes('compliance') || name.includes('risk') || 
      name.includes('governance') || name.includes('audit') || name.includes('privacy') ||
      name.includes('protection') || name.includes('vulnerability') || name.includes('threat') ||
      name.includes('encryption') || name.includes('authentication') || name.includes('authorization')) {
    return { icon: 'Shield', color: 'bg-red-500' };
  }
  
  // Default fallback
  return { icon: 'Star', color: 'bg-purple-500' };
};

export const InterviewFeedbackModal: React.FC<InterviewFeedbackModalProps> = ({
  isOpen,
  onClose,
  feedback,
}) => {
  const [hoveredSkill, setHoveredSkill] = useState<number | null>(null);
  const [isGeneratingLearnifieLink, setIsGeneratingLearnifieLink] = useState(false);
  const {showToast} = useToast()
  // Handle rating submission using interviewService
  const handleRatingSubmit = async (rating: string) => {
    if (!feedback.interviewDetails?.userInterviewId) {
      showToast('Interview ID not found', 'error');
      return;
    }

    try {
      // Use interviewService to update the user feedback rating
      const response = await interviewService.updateUserFeedbackRating(feedback.interviewDetails?.userInterviewId, rating as 'like' | 'dislike');

      if (response) {
        showToast('Thank you for your feedback!', 'success');
      } else {
        showToast('Failed to submit rating', 'error');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating. Please try again.');
  }
}

  const handleGenerateLearnifieLink = async () => {
    if (isGeneratingLearnifieLink) {
      return;
    }

    const goalPayload = buildLearnifieGoal({ feedback });

    if (!goalPayload) {
      showToast('Missing interview feedback to send to Learnifie. Please refresh and try again.', 'error');
      return;
    }

    setIsGeneratingLearnifieLink(true);

    try {
      const data = await generateLearnifieLink(goalPayload);

      window.open(data.url, '_blank', 'noopener,noreferrer');
      showToast('Opened your Learnifie improvement plan in a new tab.', 'success');
    } catch (error) {
      console.error('Error generating Learnifie link:', error);
      window.open('https://www.learnifie.com?utm_source=mocktrail&utm_medium=feedback_modal_learnifie_link', '_blank', 'noopener,noreferrer');
    } finally {
      setIsGeneratingLearnifieLink(false);
    }
  };
  
  // Process feedback categories to ensure they have icons and colors
  const processedFeedback = {
    ...feedback,
    strengths: feedback.strengths && feedback.strengths.length > 0 ? feedback.strengths : [{
      text: 'Interview Participation',
      reason: 'You participated in the interview process, which is the first step to improvement.'
    }],
    categories: feedback.categories && feedback.categories.length > 0 ? feedback.categories.map(category => {
      // If category already has icon and color, use them
      if (category.icon && category.color) {
        return category;
      }
      
      // Otherwise, assign them based on category name
      const { icon, color } = assignCategoryIconAndColor(category.name);
      return {
        ...category,
        icon: category.icon || icon,
        color: category.color || color
      };
    }) : []
  };
  
  // Use React's useEffect properly - before any conditionals
  useEffect(() => {
    if (isOpen) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Now we can use conditional return
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    // Return placeholder if dateString is not valid
    if (!dateString || dateString === 'undefined' || dateString === 'null') {
      return 'Date not available';
    }
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }).format(date);
    } catch (error) {
      console.error('[InterviewFeedbackModal] Error formatting date:', error);
      return 'Date not available';
    }
  };

  // Determine score color
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  // Determine score background
  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Return verbal assessment based on score
  const getScoreVerbal = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very Good';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Satisfactory';
    if (score >= 50) return 'Average';
    return 'Poor';
  };

  // Return impression verbal rating
  const getImpressionVerbal = (rating: number) => {
    if (rating >= 5) return 'Exceptional';
    if (rating >= 4) return 'Impressive';
    if (rating >= 3) return 'Satisfactory';
    if (rating >= 2) return 'Below Average';
    return 'Average';
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center animate-fade-in overflow-auto p-4">
        <div 
          className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-4xl border border-primary-light/20 dark:border-primary-dark/20 animate-fade-in-up max-h-[90vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-surface-light dark:bg-surface-dark border-b border-border-light dark:border-border-dark p-4 p-5 flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark flex items-center">
                <Award className="mr-3 text-text-primary-light dark:text-text-primary-dark" size={20} />
                Interview Feedback
              </h3>
              <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm mt-1">
                {processedFeedback.interviewDetails?.title || 'Interview'} • {formatDate(processedFeedback.interviewDetails?.scheduledAt || '')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-4 pr-2 md:p-5">
            {/* Interview Info */}
            <div className="mb-6 bg-background-light/50 dark:bg-background-dark/50 p-4 rounded-lg border border-border-light dark:border-border-dark">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">Job Role</p>
                  <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading">{processedFeedback.interviewDetails?.jobRole}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">Company</p>
                  <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading">{processedFeedback.interviewDetails?.company}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">Duration (used)</p>
                  <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading">{processedFeedback.interviewDetails?.minutesUsed} minutes</p>
                </div>
              </div>
            </div>

          {/* Overall Score */}
          <div className="mb-8">
            <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
              <BarChart2 size={18} className="mr-2 text-text-primary-light dark:text-text-primary-dark" />
              Overall Performance
            </h4>
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="flex items-center justify-center">
                <div className="relative w-32 h-32 flex items-center justify-center">
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <circle
                      className="text-background-light dark:text-background-dark stroke-current"
                      strokeWidth="10"
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                    ></circle>
                    <circle
                      className={`${getScoreColor(processedFeedback.overallScore || 0)} stroke-current`}
                      strokeWidth="10"
                      strokeLinecap="round"
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      strokeDasharray={`${2.5 * Math.PI * 40 * (processedFeedback.overallScore || 0) / 100} ${
                        2.5 * Math.PI * 40 * (1 - (processedFeedback.overallScore || 0) / 100)
                      }`}
                      strokeDashoffset={2.5 * Math.PI * 10}
                    ></circle>
                  </svg>
                  <div className="absolute flex flex-col items-center justify-center">
                    <span className={`text-2xl font-bold ${getScoreColor(processedFeedback.overallScore || 0)}`}>
                      {processedFeedback.overallScore || 0}%
                    </span>
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      {getScoreVerbal(processedFeedback.overallScore || 0)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-1 bg-background-light/50 dark:bg-background-dark/50 p-4 rounded-lg border border-border-light dark:border-border-dark">
                    <p className="text-text-secondary-light dark:text-text-secondary-dark mb-3 text-sm">
                          {processedFeedback.summary}
                    </p>
              </div>
            </div>
          </div>
          
          {/* Category Scores */}
          <div className="mb-8">
            <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
              <BrainCircuit size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
              Performance by Category
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {processedFeedback.categories?.map((category, index) => (
                <div 
                  key={index} 
                  className="bg-background-light/50 dark:bg-background-dark/50 p-4 rounded-lg border border-border-light dark:border-border-dark"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className={`p-1.5 rounded-full mr-2 bg-opacity-20 dark:bg-opacity-20 ${category.color || 'bg-gray-500'}`}>
                        {category.icon && getIconComponent(category.icon, 16, getColorClassFromBg(category.color || 'bg-gray-500'))}
                      </div>
                      <h5 className="font-medium text-text-primary-light dark:text-text-primary-dark">{category.name}</h5>
                    </div>
                    <span className={`font-bold ${getScoreColor(category.score)}`}>
                      {category.score}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-background-light dark:bg-background-dark rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${getScoreBg(category.score)}`}
                      style={{ width: `${category.score}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    {getScoreVerbal(category.score)}
                  </p>
                  <p className="mt-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    {category.feedback}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Skills Assessment */}
          {processedFeedback.skills && processedFeedback.skills.length > 0 && (
            <div className="mb-8">
              <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                <Star size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
                Skills Covered in Interview
              </h4>
              <div className="flex flex-wrap gap-3">
                {processedFeedback.skills?.map((skill, index) => (
                  <div 
                    key={index} 
                    className="relative"
                    onMouseEnter={() => setHoveredSkill(index)}
                    onMouseLeave={() => setHoveredSkill(null)}
                  >
                    <div className="inline-flex items-center bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-full px-3 sm:px-4 py-2 gap-2 min-w-fit max-w-full">
                      <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark whitespace-nowrap truncate max-w-[150px] sm:max-w-none">
                        {skill.name}
                      </span>
                      <span className="text-xs font-semibold text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {skill.score}/5
                      </span>
                      <Info 
                        size={14} 
                        className="text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark cursor-help transition-colors flex-shrink-0" 
                      />
                    </div>
                    
                    {/* Tooltip */}
                    <div 
                      className={`absolute z-10 -bottom-16 left-1/2 transform -translate-x-1/2 w-64
                        bg-surface-light dark:bg-surface-dark 
                        text-text-primary-light dark:text-text-primary-dark px-3 py-2 rounded-lg text-xs
                        border border-border-light dark:border-border-dark shadow-md transition-opacity duration-200
                        ${hoveredSkill === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                      `}
                      style={{
                        left: index < 2 ? '0' : 'auto',
                        right: index >= 2 ? '0' : 'auto',
                        transform: index < 2 ? 'none' : index >= 2 ? 'none' : 'translateX(-50%)'
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 
                        bg-surface-light dark:bg-surface-dark border-t border-l border-border-light dark:border-border-dark"></div>
                      <div className="text-center break-words">
                        {skill.feedback}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

            {/* AI Interviewer's Impression */}
            <div className="mb-8">
              <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                <User size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
                Interviewer's Impression
              </h4>
              <div className="bg-background-light/50 dark:bg-background-dark/50 p-4 rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex items-center">
                    <div className="bg-primary-light/10 dark:bg-primary-dark/10 rounded-full p-2 mr-3">
                      <div className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark">
                        {processedFeedback.impression?.rating || 0}/5
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                        Overall Impression
                      </p>
                      <p className="font-medium text-text-primary-light dark:text-text-primary-dark font-heading font-medium">
                        {getImpressionVerbal(processedFeedback.impression?.rating || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 border-l-0 md:border-l border-border-light dark:border-border-dark md:pl-4">
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-1">
                      Interviewer's Notes
                    </p>
                    <p className="text-base text-text-primary-light dark:text-text-primary-dark">
                      {processedFeedback.impression?.comment || 'No impression notes provided. '}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Strengths */}
            <div className="mb-8">
              <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                <ThumbsUp size={18} className="mr-2 text-green-500" />
                What You Did Well
              </h4>
              <div className="space-y-4">
                {processedFeedback.strengths && processedFeedback.strengths.length > 0 && processedFeedback.strengths?.map((strength, index) => (
                  <div 
                    key={index} 
                    className="bg-green-500/5 border border-green-500/20 dark:border-green-500/20 p-4 rounded-lg"
                  >
                    <div className="flex items-start">
                      <CheckCircle size={18} className="text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-base text-text-primary-light dark:text-text-primary-dark mb-2 font-heading font-medium">
                          {strength.text}
                        </p>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          {strength.reason || 'No reason provided.'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Areas for Improvement */}
            <div className="mb-8">
              <h4 className="text-lg font-medium mb-4 flex items-center text-text-primary-light dark:text-text-primary-dark">
                <ThumbsDown size={18} className="mr-2 text-amber-500" />
                Areas for Improvement
              </h4>
              <div className="space-y-4">
                {processedFeedback.improvements && processedFeedback.improvements.length > 0 && processedFeedback.improvements?.map((improvement, index) => (
                  <div 
                    key={index} 
                    className="bg-yellow-500/5 border border-yellow-500/20 dark:border-yellow-500/20 p-4 rounded-lg"
                  >
                    <div className="flex items-start">
                      <AlertTriangle size={18} className="text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-base text-text-primary-light dark:text-text-primary-dark mb-2 font-heading font-medium">
                          {improvement.text}
                        </p>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          {improvement.reason || 'No reason provided.'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 mt-6 pt-4 border-t border-border-light dark:border-border-dark">
              <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
                Thanks! This was helpful
              </Button>
              <Button
                variant="primary"
                onClick={handleGenerateLearnifieLink}
                loading={isGeneratingLearnifieLink}
                leftIcon={!isGeneratingLearnifieLink ? <GraduationCap size={16} /> : undefined}
                className="w-full sm:w-auto"
              >
                {isGeneratingLearnifieLink ? 'Generating...' : 'Improve with Learnifie'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

// Helper function to convert background color class to text color class
const getColorClassFromBg = (bgColor: string) => {
  const colorMap: { [key: string]: string } = {
    'bg-blue-500': 'text-blue-500',
    'bg-purple-500': 'text-purple-500',
    'bg-green-500': 'text-green-500',
    'bg-orange-500': 'text-orange-500',
    'bg-indigo-500': 'text-indigo-500',
    'bg-cyan-500': 'text-cyan-500',
    'bg-emerald-500': 'text-emerald-500',
    'bg-pink-500': 'text-pink-500',
    'bg-rose-500': 'text-rose-500',
    'bg-yellow-500': 'text-yellow-500',
    'bg-red-500': 'text-red-500',
    'bg-teal-500': 'text-teal-500',
    'bg-gray-500': 'text-gray-500',
    'bg-violet-500': 'text-violet-500',
    'bg-green-600': 'text-green-600',
    'bg-indigo-600': 'text-indigo-600',
    'bg-orange-600': 'text-orange-600',
    'bg-red-600': 'text-red-600',
    'bg-blue-600': 'text-blue-600',
    'bg-gray-600': 'text-gray-600',
  };
  
  return colorMap[bgColor] || 'text-white';
};

export default InterviewFeedbackModal; 
